import React, { useState, useEffect } from 'react';
import { trucksService } from '../services/trucksService';

// Este componente realiza diagnósticos en los contadores de mantenimiento
// para ayudar a verificar que los conteos son correctos
const MaintenanceDebugger = () => {
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [statusCounts, setStatusCounts] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        setError(null);

        // 1. Obtener conteos generales
        const countsResponse = await trucksService.getCounts();
        setCounts(countsResponse?.data || {});

        // 2. Obtener análisis detallado - esta función se añadió en trucksService
        await new Promise(resolve => setTimeout(resolve, 500)); // Pequeña pausa para asegurar que las llamadas no colisionen
        const analysisResult = await trucksService.debugMaintenanceCounts();
        setAnalysis(analysisResult?.data || {});

        // 3. Obtener conteos por tipo de unidad
        await new Promise(resolve => setTimeout(resolve, 500)); // Pequeña pausa
        const statusCountsResult = await trucksService.getStatusCountsByType('Tracto Camión');
        setStatusCounts(statusCountsResult || []);

      } catch (err) {
        console.error("Error en MaintenanceDebugger:", err);
        setError(err.message || "Error desconocido en diagnóstico");
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  if (loading) {
    return (
      <div className="bg-blue-50 p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-xl font-bold text-blue-800 mb-4">Diagnóstico de Mantenimientos</h2>
        <div className="flex items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800 mr-3"></div>
          <p>Analizando contadores de mantenimiento...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-xl font-bold text-red-800 mb-4">Error en Diagnóstico</h2>
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  // Extraer valores para comparación
  const preventivoAPI = Number(counts?.mantenimiento_preventivo || 0);
  const correctivoAPI = Number(counts?.mantenimiento_correctivo || 0);
  const preventivoAnalisis = analysis?.preventive || 0;
  const correctivoAnalisis = analysis?.corrective || 0;
  const genericoAnalisis = analysis?.generic || 0;
  
  // Extraer valores de estado por tipo
  const preventivoStatus = statusCounts?.find(item => item.name === 'M. Preventivo')?.value || 0;
  const correctivoStatus = statusCounts?.find(item => item.name === 'M. Correctivo')?.value || 0;

  // Determinar si hay discrepancias
  const hasPreventivoDiscrepancy = preventivoAPI !== preventivoAnalisis;
  const hasCorrectivoDiscrepancy = correctivoAPI !== correctivoAnalisis;
  const hasGenericDiscrepancy = genericoAnalisis > 0;
  const hasStatusDiscrepancy = preventivoAPI !== preventivoStatus || correctivoAPI !== correctivoStatus;

  return (
    <div className="bg-white p-6 rounded-lg shadow-md mb-6 border border-gray-200">
      <h2 className="text-xl font-bold text-gray-800 mb-4">Diagnóstico de Mantenimientos</h2>
      
      {/* Resumen de estado */}
      <div className="mb-6">
        <p className={`text-lg font-bold ${hasPreventivoDiscrepancy || hasCorrectivoDiscrepancy || hasGenericDiscrepancy ? 'text-red-600' : 'text-green-600'}`}>
          {hasPreventivoDiscrepancy || hasCorrectivoDiscrepancy || hasGenericDiscrepancy ? 
            '⚠️ Se detectaron discrepancias' : 
            '✅ Conteo de mantenimientos correcto'}
        </p>
      </div>

      {/* Conteos de API */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Conteos de API:</h3>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Preventivos:</p>
              <p className={`text-xl font-bold ${hasPreventivoDiscrepancy ? 'text-red-600' : 'text-gray-800'}`}>
                {preventivoAPI}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Correctivos:</p>
              <p className={`text-xl font-bold ${hasCorrectivoDiscrepancy ? 'text-red-600' : 'text-gray-800'}`}>
                {correctivoAPI}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Conteos por análisis directo */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Conteos por análisis directo:</h3>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-500">Preventivos:</p>
              <p className={`text-xl font-bold ${hasPreventivoDiscrepancy ? 'text-red-600' : 'text-gray-800'}`}>
                {preventivoAnalisis}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Correctivos:</p>
              <p className={`text-xl font-bold ${hasCorrectivoDiscrepancy ? 'text-red-600' : 'text-gray-800'}`}>
                {correctivoAnalisis}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Genéricos (sin mapear):</p>
              <p className={`text-xl font-bold ${hasGenericDiscrepancy ? 'text-red-600' : 'text-gray-800'}`}>
                {genericoAnalisis}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Conteos por tipo de unidad */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Conteos por tipo (Tracto Camión):</h3>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Preventivos:</p>
              <p className={`text-xl font-bold ${preventivoAPI !== preventivoStatus ? 'text-red-600' : 'text-gray-800'}`}>
                {preventivoStatus}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Correctivos:</p>
              <p className={`text-xl font-bold ${correctivoAPI !== correctivoStatus ? 'text-red-600' : 'text-gray-800'}`}>
                {correctivoStatus}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Detalles de vehículos en mantenimiento */}
      <div className="mt-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Vehículos en mantenimiento:</h3>
        <div className="bg-gray-50 p-4 rounded-lg overflow-auto max-h-64">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="text-left text-sm font-medium text-gray-500 py-2">ID</th>
                <th className="text-left text-sm font-medium text-gray-500 py-2">Nº Económico</th>
                <th className="text-left text-sm font-medium text-gray-500 py-2">Estado</th>
              </tr>
            </thead>
            <tbody>
              {analysis?.trucks?.length > 0 ? (
                analysis.trucks.map((truck, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="py-2 text-sm text-gray-700">{truck.id || 'N/A'}</td>
                    <td className="py-2 text-sm text-gray-700">{truck.numero_economico || 'N/A'}</td>
                    <td className={`py-2 text-sm ${truck.estatus === 'En mantenimiento' ? 'text-red-600 font-bold' : 'text-gray-700'}`}>
                      {truck.estatus}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="py-4 text-center text-gray-500">No hay vehículos en mantenimiento</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceDebugger;